// module.tide (contract §4.8.1): TideModule (OutlookModules.tsx): the
// modelled sea level from the lookback to twelve hours ahead (dashed grey,
// its wash under it), in HistoryChart's compact frame with the "now" cursor,
// each turn marked with its time, and the honesty line ("≈1.2 m range ·
// modelled, not a tide table"); or the module's line while the tide is
// pending, unavailable, negligible or missing.
//
// Where the page differs from today's module: a turn's label is its
// time, over its dot, since the feed carries no turn's height; and the next
// turn, which rides the module card's header today, is shown again over the
// chart, since a value field is shown on the page (§9.6).

import { css, h, n } from "./kit.js";
import { F, R, S } from "./module-kit.js";

const M = { top: 14, right: 8, bottom: 6, left: 30 };
const TURNS = 6;

export function moduleTide(spec) {
  const lookback =
    typeof spec.config?.lookbackHours === "number" &&
    spec.config.lookbackHours > 0
      ? spec.config.lookbackHours
      : 6;
  const nowFrac = lookback / (lookback + 12);
  const inset = `${M.top} ${M.right} ${M.bottom} ${M.left}`;
  const sea = {
    "data-tc-y": "feed",
    "data-tc-group": "s",
    "data-tc-inset": inset,
  };
  const parts = [];
  for (let k = 0; k < 5; k += 1)
    parts.push(
      h("line", { class: "g", x2: 1, "data-tc-scale": "s", "data-tc-tick": k }),
      h("text", { class: "t", "data-tc-scale": "s", "data-tc-tick": k }),
    );
  parts.push(
    h("path", {
      class: "ar",
      ...S("sea", { ...sea, "data-tc-shape": "area" }),
    }),
    h("path", { class: "ln", ...S("sea", sea) }),
    h("line", { class: "nw", y2: 1 }),
  );
  // A turn's dot and its label are drawn at the turn's point. A host-filled
  // element is a leaf, so each label is a shape in <defs> that a <use> bound
  // to the turn's point draws there: the use takes --x and --y, and the
  // shape inherits them.
  const defs = [];
  for (let k = 0; k < TURNS; k += 1) {
    defs.push(
      h(
        "g",
        { id: `tl${k}` },
        h("circle", { class: "tp-d", r: 3 }),
        h("text", { class: "tk", ...R("turn-kind", k) }),
        h("text", { class: "tt", ...R("turn-time", k) }),
      ),
    );
    parts.push(
      h("use", { class: "tp-u", href: `#tl${k}`, ...R("turn-at", k) }),
    );
  }
  parts.unshift(h("defs", {}, defs));
  const pw = `(var(--tc-w,320) - ${M.left + M.right})`;
  const ph = `(var(--tc-h,120) - ${M.top + M.bottom})`;
  return {
    html: [
      h("i", { class: "mo-flag", ...F("state") }),
      h("p", { class: "mo-msg", ...F("message") }),
      h(
        "div",
        { class: "td-body" },
        h("p", { class: "td-n" }, h("span", {}, "Next"), h("b", F("next"))),
        h(
          "svg",
          {
            class: "td",
            "data-tc-fit": "plot",
            "data-tc-window": "feed:module.sea",
            "aria-hidden": "true",
          },
          parts,
        ),
        h(
          "p",
          { class: "mo-hon td-hon" },
          h("span", { class: "td-r", ...F("range") }),
          h("span", { class: "td-sep" }, " · "),
          "modelled, not a tide table",
        ),
        h("span", { class: "td-sum", ...F("summary") }),
      ),
    ],
    css: css(`
      .mo-tide { min-height: 0; }
      .td-body { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
      .mo-flag[data-state=pending] ~ .td-body, .mo-flag[data-state=unavailable] ~ .td-body,
        .mo-flag[data-state=negligible] ~ .td-body, .mo-flag[data-state=none] ~ .td-body { display: none; }
      .td { display: block; width: 100%; height: 120px; flex: 1 1 auto; min-height: 120px; overflow: visible; font-family: var(--tc-font-sans); }
      .td text { font-size: 11px; fill: var(--muted); font-variant-numeric: tabular-nums; }
      .td .t { text-anchor: end; transform: translate(${M.left - 6}px, calc(${M.top}px + (1 - var(--frac,0)) * ${ph} * 1px + 3.5px)); }
      .td .g { stroke: var(--grid); stroke-width: 0.5; vector-effect: non-scaling-stroke;
        transform: translate(${M.left}px, calc(${M.top}px + (1 - var(--frac,0)) * ${ph} * 1px)) scaleX(calc(${pw})); }
      .td .ln { fill: none; stroke: var(--quiet); stroke-width: 2.5; stroke-dasharray: 3 6; stroke-linejoin: round; }
      .td .ar { fill: var(--quiet); fill-opacity: 0.14; stroke: none; }
      .td .nw { stroke: var(--grid); stroke-width: 1; vector-effect: non-scaling-stroke;
        transform: translate(calc(${M.left}px + ${n(nowFrac)} * ${pw} * 1px), ${M.top}px) scaleY(calc(${ph})); }
      .td .tp-u { transform: translate(calc(${M.left}px + var(--x,0) * ${pw} * 1px), calc(${M.top}px + (1 - var(--y,0)) * ${ph} * 1px)); }
      .td .tp-d { fill: var(--quiet); stroke: var(--background); stroke-width: 2; }
      .td .tk { display: none; }
      .td .tt { text-anchor: middle; font-weight: 600; fill: var(--text); transform: translateY(-8px); }
      .td [data-quality=missing] { display: none; }
      .td-r:empty + .td-sep { display: none; }
      .td-hon { margin-top: 4px; }
      .td-n { display: flex; align-items: baseline; gap: 6px; font-size: 12px; color: var(--muted); }
      .td-n b { font-size: 15px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }
      .td-n b[data-quality=missing] { color: var(--muted); }
      .td-sum { display: none; }
    `),
  };
}
